"use client";

import type { ReactNode } from "react";
import { useModalSurface } from "@/lib/hooks/useModalSurface";

/**
 * The mobile chrome shared by EmojiPicker and EmojiReactionPicker: a
 * near-full-height bottom sheet (Messenger-style) instead of the small
 * anchored popover those use above the `sm` breakpoint. Each caller decides
 * mobile vs desktop itself (via `window.matchMedia`, checked once when the
 * picker opens — see the `isMobile` state in each) and swaps this in for its
 * usual `absolute`-positioned popover wrapper; the search box + EmojiGrid
 * content passed in as `children` is unchanged either way.
 *
 * IT IS A MODAL AND NOW SAYS SO (OSO-638). It covered the screen while focus
 * stayed on the trigger behind it, Tab walked straight out into the page under
 * the backdrop, and it announced itself as nothing — an unnamed `div`. It also
 * had no close control a keyboard could reach: the only visible affordance was
 * the drag handle, which is a decorative `<div>`, so the "Luk" button below is
 * the first one there has ever been.
 *
 * ESCAPE IS ANSWERED HERE, and the first version of this file got that wrong.
 *
 * It passed `escape: false` on the reasoning that the caller already handles
 * Escape. That is true of `EmojiPicker` — and false of `EmojiReactionPicker`,
 * which passes `escape: false` to its own `useDismissable`. So the reaction
 * sheet became a focus-trapping `aria-modal` with NO way out on a keyboard,
 * and worse: registering an Escape layer at all demotes whatever the sheet
 * sits inside by containment, so the lightbox behind it stopped answering
 * Escape too. Reachable in three taps on a phone — lightbox, comment,
 * reaction picker — and the key then did nothing at all.
 *
 * Answering here is safe for BOTH callers rather than only one.
 * `EmojiPicker`'s `useDismissable` listens in the capture phase, so it still
 * runs first, closes, and marks the key spent; this handler checks
 * `defaultPrevented` and stands down. `EmojiReactionPicker` has no such
 * listener, so this one is the only answer — which is the point.
 *
 * (The audit, §2.2, says the only dismissal is a backdrop tap. That was
 * already wrong for `EmojiPicker` when it was written, for the capture-phase
 * reason above. It was right about the reaction picker for a different reason
 * than it gave.)
 */
export function EmojiPickerSheet({
  onClose,
  children,
  label = "Vælg emoji",
}: {
  onClose: () => void;
  children: ReactNode;
  /** Names the sheet. Both callers open onto a grid of emoji; the reaction
   *  picker passes its own wording. */
  label?: string;
}) {
  // `open: true` always: this component is mounted only while the picker is
  // open, so "open" and "rendered" are the same fact. The hook still needs it,
  // because it is what its focus-capture and restore hang off.
  const panelRef = useModalSurface<HTMLDivElement>({ open: true, onClose });

  return (
    <>
      <div
        className="fixed inset-0 z-[150] bg-black/40 animate-fade-in"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className="fixed inset-x-0 bottom-0 z-[150] flex max-h-[85vh] flex-col rounded-t-2xl bg-surface shadow-xl animate-fade-in outline-none"
      >
        <div className="flex items-center justify-between px-2 pt-2 pb-1 shrink-0">
          {/* Balances the close button so the handle stays centred. */}
          <span className="w-16" aria-hidden="true" />
          <span className="h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-600" />
          <button
            type="button"
            onClick={onClose}
            className="w-16 px-2 py-1 text-sm font-medium text-primary rounded-lg hover:bg-surface-2 transition-colors"
          >
            Luk
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {children}
        </div>
      </div>
    </>
  );
}
